'use client';

import Image from 'next/image';
import { Book } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookOpen, MapPin, Barcode } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BookCardProps {
  book: Book;
  onEdit?: (book: Book) => void;
  onBorrow?: (book: Book) => void;
  view?: 'grid' | 'list';
}

const statusConfig = {
  available: { label: '可借阅', color: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
  borrowed: { label: '已借出', color: 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30' },
  reserved: { label: '已预约', color: 'bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/30' },
  maintenance: { label: '维护中', color: 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30' },
};

export function BookCard({ book, onEdit, onBorrow, view = 'grid' }: BookCardProps) {
  const status = statusConfig[book.status];

  if (view === 'list') {
    return (
      <div className="flex items-center gap-4 p-4 rounded-lg bg-[#112240] border border-[#1d3557] hover:border-[#64FFDA]/30 transition-all">
        <div className="relative w-16 h-20 rounded overflow-hidden flex-shrink-0">
          <Image
            src={book.cover}
            alt={book.title}
            fill
            className="object-cover"
            crossOrigin="anonymous"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-[#E6F1FF] truncate">{book.title}</h3>
            <Badge className={cn("text-xs", status.color)}>
              {status.label}
            </Badge>
          </div>
          <p className="text-sm text-[#8892B0]">{book.author}</p>
          <div className="flex items-center gap-4 mt-1 text-xs text-[#8892B0]">
            <span className="flex items-center gap-1">
              <Barcode className="w-3 h-3" />
              {book.isbn}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {book.location}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-[#1d3557] text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#1d3557]"
            onClick={() => onEdit?.(book)}
          >
            编辑
          </Button>
          <Button
            size="sm"
            className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90"
            disabled={book.status !== 'available'}
            onClick={() => onBorrow?.(book)}
          >
            <BookOpen className="w-4 h-4 mr-1" />
            借阅
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group rounded-xl bg-[#112240] border border-[#1d3557] overflow-hidden card-hover">
      <div className="relative aspect-[3/4] overflow-hidden">
        <Image
          src={book.cover}
          alt={book.title}
          fill
          className="object-cover transition-transform duration-300 group-hover:scale-105"
          crossOrigin="anonymous"
        />
        <div className="absolute top-3 right-3">
          <Badge className={cn("text-xs", status.color)}>
            {status.label}
          </Badge>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#020C1B] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-medium text-[#E6F1FF] line-clamp-1" title={book.title}>
          {book.title}
        </h3>
        <p className="text-sm text-[#8892B0] line-clamp-1">{book.author}</p>
        <div className="flex items-center justify-between text-xs text-[#8892B0]">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {book.location}
          </span>
          <span>库存: {book.quantity}</span>
        </div>
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 border-[#1d3557] text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#1d3557]"
            onClick={() => onEdit?.(book)}
          >
            编辑
          </Button>
          <Button
            size="sm"
            className="flex-1 bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90"
            disabled={book.status !== 'available'}
            onClick={() => onBorrow?.(book)}
          >
            借阅
          </Button>
        </div>
      </div>
    </div>
  );
}
